import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';

import { API_BASE_URL } from '@mm-aggregator/drf-dynamics';
import { AuthService } from '../services/auth.service';

/**
 * Adds Bearer token to API requests for token-based authentication.
 * Gets the token from AuthService and adds it to the Authorization header.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const apiBaseUrl = inject(API_BASE_URL);

  const base = typeof apiBaseUrl === 'string' && apiBaseUrl.length > 0 ? apiBaseUrl.replace(/\/$/, '') : '';
  const isApiRequest = base !== '' && (req.url === base || req.url.startsWith(base + '/'));

  // Skip adding token for login endpoint
  const isLoginEndpoint = req.url.includes('/auth/login');

  if (isApiRequest && !isLoginEndpoint) {
    const token = auth.getToken();
    if (token) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
  }

  return next(req);
};

/**
 * Handles 401 Unauthorized responses by attempting a silent token refresh.
 *
 * Flow:
 * 1. A request returns 401 (token expired).
 * 2. If a refresh is not already running, start one.
 * 3. If the refresh succeeds, **retry** the original request with the new token.
 * 4. If the refresh fails (or the request is itself auth-related), redirect to login.
 *
 * Concurrent requests that hit 401 while a refresh is in-flight will wait
 * for the same refresh and then retry together — no duplicate refresh calls.
 */
export const unauthorizedInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  // Never intercept auth endpoints to avoid loops
  const isAuthEndpoint =
    req.url.includes('/auth/login') ||
    req.url.includes('/auth/logout') ||
    req.url.includes('/auth/refresh');

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || isAuthEndpoint) {
        return throwError(() => error);
      }

      // Attempt refresh
      const refresh$ = auth.isRefreshing ? auth.waitForRefresh() : auth.refreshToken();

      return refresh$.pipe(
        switchMap((success) => {
          if (success) {
            // Retry the original request with the fresh token
            const newToken = auth.getToken();
            const retryReq = newToken
              ? req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } })
              : req;
            return next(retryReq);
          }

          // Refresh failed — redirect to login
          auth.clearLocalState();
          const returnUrl = router.url && router.url !== '/login' ? router.url : '/app';
          router.navigate(['/login'], {
            replaceUrl: true,
            queryParams: { returnUrl },
          });
          return throwError(() => error);
        }),
        catchError(() => {
          // Safety net — if retry also fails, redirect to login
          auth.clearLocalState();
          const returnUrl = router.url && router.url !== '/login' ? router.url : '/app';
          router.navigate(['/login'], {
            replaceUrl: true,
            queryParams: { returnUrl },
          });
          return throwError(() => error);
        })
      );
    })
  );
};
